<template>
  <div class="entrance-exam-datetime">
    <el-form
      ref="examForm"
      class="entrance-exam-form"
      :model="examForm"
      :rules="examRules"
      label-position="top"
    >
      <div class="row">
        <div class="col-md-6">
          <el-form-item label="Ngày thi" prop="examDate">
            <el-select
              v-model="examForm.examDate"
              class="w-100 entrance-exam-select"
              :class="{ 'is-filled': examForm.examDate }"
              placeholder="Chọn ngày thi"
              filterable
              popper-class="entrance-exam-select-dropdown"
              :disabled="examScheduleLoading || !centerId"
              :loading="examScheduleLoading"
              @change="onExamDateChange"
            >
              <el-option
                v-for="opt in entranceExamDateOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </el-form-item>
        </div>
        <div class="col-md-6">
          <el-form-item label="Giờ thi" prop="examTime">
            <el-select
              v-model="examForm.examTime"
              class="w-100 entrance-exam-select"
              :class="{ 'is-filled': examForm.examTime }"
              :placeholder="timePlaceholder"
              filterable
              popper-class="entrance-exam-select-dropdown"
              :disabled="!examForm.examDate || !entranceExamTimeOptions.length"
              @change="emitState"
            >
              <el-option
                v-for="opt in entranceExamTimeOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </el-form-item>
        </div>
      </div>
    </el-form>
    <p v-if="examDateAvailabilityError" class="entrance-exam-form__availability-error mb-0">
      {{ examDateAvailabilityError }}
    </p>
  </div>
</template>

<script>
import { GET_ENTRANCE_EXAM_SCHEDULE } from "@/core/services/store/customer/entrance.module";

export default {
  name: "EntranceExamDateTimeFields",
  props: {
    centerId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      examScheduleRequestId: 0,
      examScheduleLoading: false,
      examSchedules: [],
      entranceExamDateOptions: [],
      entranceExamTimeOptions: [],
      examDateAvailabilityError: "",
      examForm: {
        examDate: "",
        examTime: ""
      },
      examRules: {
        examDate: [
          { required: true, message: "Vui lòng chọn ngày thi", trigger: ["change", "blur"] }
        ],
        examTime: [
          { required: true, message: "Vui lòng chọn giờ thi", trigger: ["change", "blur"] }
        ]
      }
    };
  },
  computed: {
    timePlaceholder() {
      if (!this.examForm.examDate) return "Vui lòng chọn ngày thi trước";
      return this.entranceExamTimeOptions.length ? "Chọn giờ thi" : "Không còn ca thi";
    }
  },
  watch: {
    centerId: {
      immediate: true,
      handler(val) {
        this.resetLocalState();
        if (val) {
          this.fetchExamSchedules();
        }
      }
    }
  },
  methods: {
    resetLocalState() {
      this.examScheduleRequestId += 1;
      this.examForm = { examDate: "", examTime: "" };
      this.clearExamScheduleState();
      this.examScheduleLoading = false;
      this.emitState();
    },
    clearExamScheduleState() {
      this.examSchedules = [];
      this.entranceExamDateOptions = [];
      this.entranceExamTimeOptions = [];
      this.examDateAvailabilityError = "";
    },
    clearSelectedExamState() {
      this.entranceExamTimeOptions = [];
      this.examDateAvailabilityError = "";
    },
    async fetchExamSchedules() {
      const requestId = ++this.examScheduleRequestId;
      const centerId = this.centerId;
      this.examScheduleLoading = true;
      try {
        const res = await this.$store.dispatch(GET_ENTRANCE_EXAM_SCHEDULE, {
          center_id: centerId
        });
        if (requestId !== this.examScheduleRequestId || centerId !== this.centerId) {
          return;
        }
        const examSchedules = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : [];
        this.examSchedules = !res?.error ? examSchedules : [];
        this.entranceExamDateOptions = this.examSchedules.map((schedule) => ({
          value: schedule.exam_date,
          label: this.formatExamDateLabel(schedule.exam_date)
        }));
      } catch {
        if (requestId !== this.examScheduleRequestId) {
          return;
        }
        this.clearExamScheduleState();
      } finally {
        if (requestId === this.examScheduleRequestId) {
          this.examScheduleLoading = false;
          this.emitState();
        }
      }
    },
    getSelectedExamSchedule() {
      return this.examSchedules.find((schedule) => schedule.exam_date === this.examForm.examDate) || null;
    },
    createLocalDate(dateValue, timeValue = "00:00:00") {
      const [year, month, day] = String(dateValue).split("-").map((part) => Number(part));
      const [hours, minutes, seconds = 0] = String(timeValue).split(":").map((part) => Number(part));
      return new Date(year, month - 1, day, hours, minutes, seconds);
    },
    isSameCalendarDay(left, right) {
      return (
        left.getFullYear() === right.getFullYear() &&
        left.getMonth() === right.getMonth() &&
        left.getDate() === right.getDate()
      );
    },
    isShiftSelectable(examDate, shift) {
      if (!examDate || !shift) return false;
      const registered = Number(shift.registration_count || 0);
      const maxSlots = Number(shift.max_slots || 0);
      if (!Number.isFinite(registered) || !Number.isFinite(maxSlots) || registered >= maxSlots) {
        return false;
      }
      const selectedDate = this.createLocalDate(examDate);
      const now = new Date();
      if (!this.isSameCalendarDay(selectedDate, now)) {
        return true;
      }
      const startTime = this.createLocalDate(examDate, shift.start_time);
      if (Number.isNaN(startTime.getTime())) {
        return false;
      }
      return startTime.getTime() > now.getTime();
    },
    getSelectableShifts(schedule) {
      const examDate = schedule?.exam_date;
      const shifts = Array.isArray(schedule?.exam_shifts) ? schedule.exam_shifts : [];
      return shifts.filter((shift) => this.isShiftSelectable(examDate, shift));
    },
    formatExamDateLabel(examDate) {
      const parsed = this.createLocalDate(examDate);
      if (Number.isNaN(parsed.getTime())) {
        return examDate;
      }
      const day = String(parsed.getDate()).padStart(2, "0");
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      return `${day}-${month}-${parsed.getFullYear()}`;
    },
    formatShiftLabel(shift) {
      const startTime = String(shift?.start_time || "").slice(0, 5);
      const endTime = String(shift?.end_time || "").slice(0, 5);
      if (!startTime && !endTime) {
        return "Ca thi";
      }
      return `${startTime}${endTime ? ` - ${endTime}` : ""}`;
    },
    syncSelectedExamDateState() {
      const selectedSchedule = this.getSelectedExamSchedule();
      if (!selectedSchedule) {
        this.clearSelectedExamState();
        return;
      }
      const selectableShifts = this.getSelectableShifts(selectedSchedule);
      this.entranceExamTimeOptions = selectableShifts.map((shift) => ({
        value: String(shift.id),
        label: this.formatShiftLabel(shift)
      }));
      if (!selectableShifts.length) {
        this.examForm.examTime = "";
        this.examDateAvailabilityError = "Lịch thi trong ngày chọn đã hết. Vui lòng chọn ngày khác.";
        return;
      }
      this.examDateAvailabilityError = "";
      if (
        this.examForm.examTime &&
        !this.entranceExamTimeOptions.some((option) => option.value === String(this.examForm.examTime))
      ) {
        this.examForm.examTime = "";
      }
    },
    onExamDateChange() {
      this.examForm.examTime = "";
      this.syncSelectedExamDateState();
      this.emitState();
    },
    emitState() {
      const markingExamShiftId = this.examForm.examTime ? Number(this.examForm.examTime) : null;
      this.$emit("state-change", {
        marking_exam_shift_id: markingExamShiftId,
        has_error: !!this.examDateAvailabilityError,
        error_message: this.examDateAvailabilityError,
        is_complete: !!markingExamShiftId && !this.examDateAvailabilityError
      });
    },
    validateForm() {
      return new Promise((resolve) => {
        this.syncSelectedExamDateState();
        this.emitState();
        if (this.examDateAvailabilityError) {
          resolve(false);
          return;
        }
        this.$refs.examForm.validate((valid) => {
          resolve(valid);
        });
      });
    }
  }
};
</script>

<style scoped>
.entrance-exam-form >>> .el-form-item__label::before {
  color: #e03131;
  content: "* ";
}

.entrance-exam-form__availability-error {
  color: #e03131;
  font-size: 12px;
  line-height: 1.35;
  margin-top: -8px;
  padding-bottom: 2px;
}
</style>
